import type { ItemSummaryPageRows, ItemSummaryRow } from "./ItemSummaryPage";
import type { A4Page, A4PageItem, BayWithA4Pages } from "./types";

interface MeasuredRow<T> {
  row: T;
  height: number;
}

export interface MeasuredTable {
  capacity: number;
  rowHeights: number[];
}

export const measurePdfTable = (table: HTMLElement): MeasuredTable => {
  const scroller = table.firstElementChild as HTMLElement | null;
  const rows = Array.from(table.querySelectorAll<HTMLElement>("[data-pdf-row]"));
  const firstRow = rows[0];
  if (!scroller) {
    throw new Error("PDF table has no measurable content");
  }

  const tableRect = table.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const header = scroller.firstElementChild?.firstElementChild as HTMLElement | null;
  const headerHeight = firstRow
    ? firstRow.getBoundingClientRect().top - scrollerRect.top
    : (header?.getBoundingClientRect().height ?? 0);
  const capacity = tableRect.height - headerHeight - 2;
  const rowHeights = rows.map((row) => row.getBoundingClientRect().height);

  if (capacity <= 0 || rowHeights.some((height) => height <= 0)) {
    throw new Error("PDF table has no measurable space for rows");
  }

  return { capacity, rowHeights };
};

export const paginateMeasuredRows = <T>(
  rows: MeasuredRow<T>[],
  capacity: number,
  maxRows: number,
): T[][] => {
  const pages: T[][] = [];
  let page: T[] = [];
  let usedHeight = 0;

  for (const { row, height } of rows) {
    if (height > capacity) {
      throw new Error("A PDF table row is taller than one page");
    }
    if (page.length && (page.length >= maxRows || usedHeight + height > capacity)) {
      pages.push(page);
      page = [];
      usedHeight = 0;
    }
    page.push(row);
    usedHeight += height;
  }

  if (page.length) pages.push(page);
  return pages;
};

export const paginateMeasuredBayRows = (
  rows: MeasuredRow<A4PageItem>[],
  capacity: number,
  maxRows = 26,
): A4Page[] => {
  const pages: A4Page[] = [];
  let page: A4Page = [];
  let usedHeight = 0;
  let currentShelf: MeasuredRow<Extract<A4PageItem, { type: "SHELF" }>> | null = null;

  const flush = () => {
    if (page.length) pages.push(page);
    page = [];
    usedHeight = 0;
  };

  for (let index = 0; index < rows.length; index++) {
    const entry = rows[index];
    if (!entry) continue;

    if (entry.row.type === "SHELF") {
      currentShelf = entry as MeasuredRow<Extract<A4PageItem, { type: "SHELF" }>>;
      const next = rows[index + 1];
      const requiredHeight = entry.height + (next?.row.type === "ITEM" ? next.height : 0);
      const requiredRows = next?.row.type === "ITEM" ? 2 : 1;
      if (
        page.length &&
        (usedHeight + requiredHeight > capacity || page.length + requiredRows > maxRows)
      ) {
        flush();
      }
      if (entry.height > capacity) throw new Error("A PDF shelf header is taller than one page");
      page.push(entry.row);
      usedHeight += entry.height;
      continue;
    }

    if (entry.height > capacity) throw new Error("A PDF item row is taller than one page");
    if (page.length >= maxRows || usedHeight + entry.height > capacity) {
      flush();
      if (currentShelf) {
        if (currentShelf.height + entry.height > capacity) {
          throw new Error("A PDF shelf header and item cannot fit together");
        }
        page.push(currentShelf.row);
        usedHeight += currentShelf.height;
      }
    }
    page.push(entry.row);
    usedHeight += entry.height;
  }

  flush();
  return pages;
};

export const repaginateBayTables = (
  bays: BayWithA4Pages[],
  measurements: MeasuredTable[],
): BayWithA4Pages[] => {
  let tableIndex = 0;
  return bays.map(({ bay, pages }) => {
    const measuredRows: MeasuredRow<A4PageItem>[] = [];
    let capacity = Number.POSITIVE_INFINITY;

    for (const page of pages) {
      const measurement = measurements[tableIndex++];
      if (!measurement || measurement.rowHeights.length !== page.length) {
        throw new Error("PDF bay table measurement does not match its rows");
      }
      capacity = Math.min(capacity, measurement.capacity);
      page.forEach((row, index) => {
        const previousRow = measuredRows[measuredRows.length - 1]?.row;
        const isRepeatedHeader =
          index === 0 &&
          row.type === "SHELF" &&
          previousRow?.type === "ITEM" &&
          previousRow.shelfName === row.shelfName;
        if (!isRepeatedHeader) {
          measuredRows.push({ row, height: measurement.rowHeights[index] ?? 0 });
        }
      });
    }

    return { bay, pages: paginateMeasuredBayRows(measuredRows, capacity) };
  });
};

export const repaginateSummaryTables = (
  pages: ItemSummaryPageRows[],
  newTables: MeasuredTable[],
  deletedTables: MeasuredTable[],
): ItemSummaryPageRows[] => {
  const collect = (side: "newItemRows" | "deletedItemRows", tables: MeasuredTable[]) => {
    const rows: MeasuredRow<ItemSummaryRow>[] = [];
    let capacity = Number.POSITIVE_INFINITY;
    pages.forEach((page, pageIndex) => {
      const table = tables[pageIndex];
      const showEmptyRow = side === "newItemRows" ? page.showNewEmptyRow : page.showDeletedEmptyRow;
      const expectedRowCount = page[side].length || (showEmptyRow ? 1 : 0);
      if (!table || table.rowHeights.length !== expectedRowCount) {
        throw new Error("PDF summary table measurement does not match its rows");
      }
      capacity = Math.min(capacity, table.capacity);
      page[side].forEach((row, rowIndex) => {
        rows.push({ row, height: table.rowHeights[rowIndex] ?? 0 });
      });
    });
    return paginateMeasuredRows(rows, capacity, 24);
  };

  const newPages = collect("newItemRows", newTables);
  const deletedPages = collect("deletedItemRows", deletedTables);
  const pageCount = Math.max(1, newPages.length, deletedPages.length);
  const firstPage = pages[0];

  return Array.from({ length: pageCount }, (_, index) => ({
    newItemRows: newPages[index] ?? [],
    deletedItemRows: deletedPages[index] ?? [],
    showNewEmptyRow: index === 0 && Boolean(firstPage?.showNewEmptyRow),
    showDeletedEmptyRow: index === 0 && Boolean(firstPage?.showDeletedEmptyRow),
    hasNoDeliveryHistory: index === 0 && Boolean(firstPage?.hasNoDeliveryHistory),
    hasComparisonError: index === 0 && Boolean(firstPage?.hasComparisonError),
  }));
};

export const isPdfTableOverflowing = (table: HTMLElement) => {
  const lastRow = Array.from(table.querySelectorAll<HTMLElement>("[data-pdf-row]")).at(-1);
  return Boolean(
    lastRow && lastRow.getBoundingClientRect().bottom > table.getBoundingClientRect().bottom - 1,
  );
};
